import { captureException } from '@sentry/react';
import { useCallback, useMemo } from 'react';
import { mutate } from 'swr';

import { ticketsPartialUpdate } from '~/data/generated/endpoints/tickets/tickets';
import { useTickets } from '~/data/hooks/useTickets';
import { getInvoiceMutator, getTicketMutator } from '~/data/hooks/utils';
import type { Invoice } from '~/data/schemas/invoice';
import type { API_TicketCreateRequest, Ticket } from '~/data/schemas/ticket';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';

/**
 * Linking and unlinking tickets on an invoice, plus the property's tickets as pickable
 * options. Extracted from `InvoiceOverview` so the drawer's Overview tab and the detail
 * page's Related tab drive the same code.
 */
export function useInvoiceTicketLinking(invoice: Invoice | undefined) {
  const { showEntityActionSnackbar } = useLAISnackbar();

  // Held until the invoice is in hand: `property: undefined` is not "no property",
  // it is "no filter", so firing early would pull every ticket the user can see
  // and offer them all as options for a moment.
  const { tickets: allPropertyTickets, isLoading } = useTickets(
    { property: invoice?.property?.id },
    invoice?.property?.id !== undefined,
  );

  const ticketOptions = useMemo(() => {
    if (!allPropertyTickets) return [];
    return allPropertyTickets.map((ticket) => ({ ticket, isLinked: !!ticket.invoice }));
  }, [allPropertyTickets]);

  const handleTicketError = useCallback(
    (error: unknown) => {
      captureException(error);
      showEntityActionSnackbar(
        { name: 'Ticket', action: ActionEnum.Fail, id: undefined },
        { variant: 'error' },
      );
    },
    [showEntityActionSnackbar],
  );

  const setTicketInvoice = useCallback(
    async (ticket: Ticket, invoiceId: string | null) => {
      try {
        // A ticket whose Property was deleted (`Ticket.property` is a SET_NULL FK) has a null
        // `property` on read, but the write serializer requires it — so fail fast with a message
        // naming the ticket rather than crash on `.id` or earn a 400 (AMP-585/AMP-593).
        if (!ticket.property) {
          throw new Error(`Ticket ${ticket.number} has no associated property`);
        }
        const ticketPayload: API_TicketCreateRequest = {
          ...ticket,
          property: ticket.property.id,
          invoice: invoiceId,
          carsServiced: ticket.carsServiced.map((car) => car.id),
          serviceProviderGroup: ticket.serviceProviderGroup?.id ?? null,
        };
        // PATCH update via the generated SDK; the payload keeps the hand-written
        // API_TicketCreateRequest shape, so cast at the boundary (AMP-507).
        await ticketsPartialUpdate(
          ticket.id,
          ticketPayload as unknown as Parameters<typeof ticketsPartialUpdate>[1],
        );
        if (invoice?.id) void mutate(getInvoiceMutator(invoice.id));
        void mutate(getTicketMutator(ticket.id));
        showEntityActionSnackbar(
          { name: 'Ticket', action: ActionEnum.Update, id: ticket.number },
          { variant: 'info' },
        );
      } catch (error) {
        handleTicketError(error);
      }
    },
    [invoice?.id, showEntityActionSnackbar, handleTicketError],
  );

  // Without an invoice there is nothing to link the ticket to. The old `?? ''`
  // fallback sent the PATCH anyway, which either 400s or, worse, writes an empty
  // invoice onto a ticket that had one.
  const onAddTicket = useCallback(
    async (ticket: Ticket) => {
      if (!invoice?.id) return;
      await setTicketInvoice(ticket, invoice.id);
    },
    [setTicketInvoice, invoice?.id],
  );

  const onRemoveTicket = useCallback(
    (ticket: Ticket) => setTicketInvoice(ticket, null),
    [setTicketInvoice],
  );

  return { ticketOptions, isLoading, onAddTicket, onRemoveTicket };
}
